import React, { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useFileDrop } from '@/hooks/useFileDrop'
import { filterAllowedUploadFiles } from '@/utils/uploadFiles'
import DragUploadOverlay from './DragUploadOverlay'

interface FileDropProps {
  onFiles: (files: File[]) => void
  onFileMismatch?: (files: File[]) => void
  fullViewport?: boolean
  style?: React.CSSProperties
  children: React.ReactNode
}

function FileDrop({ onFiles, onFileMismatch, fullViewport, style, children }: FileDropProps) {
  const { t } = useTranslation()
  const filteredOnFiles = useCallback(
    (files: File[]) => {
      const allowed = filterAllowedUploadFiles(files)
      const rejected = files.filter((f) => !allowed.includes(f))
      if (rejected.length) onFileMismatch?.(rejected)
      if (allowed.length) onFiles(allowed)
    },
    [onFiles, onFileMismatch],
  )
  const { isDraggingFiles, handleDragEnter, handleDragOver, handleDragLeave, handleDrop } =
    useFileDrop(filteredOnFiles)

  return (
    <div
      style={{ position: 'relative', ...style }}
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      {children}
      {isDraggingFiles && (
        <DragUploadOverlay
          fullViewport={fullViewport}
          header={t('Drop to upload your files')}
          description={t('You can drop multiple files. Each should be .jpg, .png, or .heic and 10 MB or less.')}
          onDrop={handleDrop}
          onDragLeave={handleDragLeave}
        />
      )}
    </div>
  )
}

export default FileDrop
